import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { wellnessStateApi } from "@/lib/api";
import { toast } from "sonner";

const WellnessStateContext = createContext(null);

export const WellnessStateProvider = ({ children }) => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState("idle");
  const saveTimer = useRef(null);
  const pendingSummary = useRef("Wellness updated");

  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await wellnessStateApi.get(); setState(d.state); }
    catch { toast.error("Could not load your wellness data"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const doSave = useCallback(async (next, summary) => {
    setSaveStatus("saving");
    try { await wellnessStateApi.save(next, summary); setSaveStatus("saved"); setTimeout(() => setSaveStatus("idle"), 1500); }
    catch { setSaveStatus("error"); toast.error("Failed to save changes"); }
  }, []);

  const update = useCallback((updater, summary = "Wellness updated") => {
    setState((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      pendingSummary.current = summary;
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => doSave(next, pendingSummary.current), 650);
      return next;
    });
  }, [doSave]);

  const saveNow = useCallback((summary) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    if (state) doSave(state, summary || pendingSummary.current);
  }, [state, doSave]);

  return (
    <WellnessStateContext.Provider value={{ state, setState, update, saveNow, loading, saveStatus, reload: load }}>
      {children}
    </WellnessStateContext.Provider>
  );
};

export const useWellnessState = () => useContext(WellnessStateContext);
